/**
 * Universe Preferences Hook
 * 
 * Manages user preferences for constellation theme, reduce motion, and power saver.
 * 
 * Architecture (NO DRAMA pattern):
 * - Backend is source-of-truth after first fetch succeeds
 * - localStorage is just a fast placeholder for offline/initial render
 * - Never PATCH while applying backend prefs (isHydratingRef)
 * - Never PATCH if prefs haven't changed (lastSavedJsonRef)
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { DEFAULT_UNIVERSE_THEME } from "./universeThemes";
import { track } from "../../lib/analytics";
import { toast } from "sonner";

const KEY = "unitylove_universe_prefs_v1";
const API_BASE = window.location.origin;
const DEBOUNCE_MS = 1500;

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

// API helpers
async function fetchVibe() {
  const token = localStorage.getItem('token');
  if (!token) return null;

  const res = await fetch(`${API_BASE}/api/constellation/vibe`, {
    headers: { Authorization: `Bearer ${token}` }
  });

  if (!res.ok) return null;

  const data = await res.json();
  if (data.success && data.prefs) {
    return {
      theme: data.prefs.theme || DEFAULT_UNIVERSE_THEME,
      reduceMotion: data.prefs.reduceMotion ?? false,
      powerSaver: data.prefs.powerSaver ?? false,
    };
  }
  return null;
}

async function patchVibe(prefs) {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No token');

  const res = await fetch(`${API_BASE}/api/constellation/vibe`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(prefs),
  });

  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
}

export function useUniversePrefs() {
  // Check system preference for reduced motion
  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || false;
  }, []);

  // NO DRAMA: Lock saves while hydrating from backend
  const isHydratingRef = useRef(true);
  // NO DRAMA: Track last saved value to skip no-op saves
  const lastSavedJsonRef = useRef(null);

  // Load preferences from localStorage first (instant placeholder)
  const [prefs, setPrefs] = useState(() => {
    const raw = typeof window !== "undefined" ? localStorage.getItem(KEY) : null;
    const parsed = raw ? safeParse(raw) : null;
    const initial = {
      theme: parsed?.theme || DEFAULT_UNIVERSE_THEME,
      reduceMotion: parsed?.reduceMotion ?? prefersReducedMotion,
      powerSaver: parsed?.powerSaver ?? false,
    };
    // Initialize lastSavedJson to prevent immediate save of placeholder
    lastSavedJsonRef.current = JSON.stringify(initial);
    return initial;
  });

  // Fetch from backend on mount (once) - backend is source-of-truth
  useEffect(() => {
    let cancelled = false;

    (async () => {
      console.log('[UniversePrefs] Fetching from backend...');
      
      try {
        const serverPrefs = await fetchVibe();

        if (cancelled) return;

        if (serverPrefs) {
          console.log('[UniversePrefs] Applying backend prefs:', serverPrefs);

          // Lock saves during hydration
          isHydratingRef.current = true;
          setPrefs(serverPrefs);

          // Keep storage aligned to backend
          const json = JSON.stringify(serverPrefs);
          localStorage.setItem(KEY, json);
          lastSavedJsonRef.current = json;

          // Unlock saves on next tick (prevents same-render save loop)
          setTimeout(() => { 
            isHydratingRef.current = false; 
            console.log('[UniversePrefs] Hydration complete, saves unlocked');
          }, 0);
        } else {
          console.log('[UniversePrefs] No backend prefs, using localStorage');
          isHydratingRef.current = false;
        }
      } catch (err) {
        console.warn('[UniversePrefs] Backend fetch failed:', err);
        isHydratingRef.current = false;
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // Save to backend when prefs change (debounced, with guards)
  useEffect(() => {
    const json = JSON.stringify(prefs);

    // Guard 1: Don't save while hydrating from backend
    if (isHydratingRef.current) {
      console.log('[UniversePrefs] Skip save (hydrating)');
      return;
    }

    // Guard 2: Don't save if nothing changed
    if (json === lastSavedJsonRef.current) {
      console.log('[UniversePrefs] Skip save (no change)');
      return;
    }

    // Track analytics
    track("vibe_changed", {
      theme: prefs.theme,
      reduce_motion: !!prefs.reduceMotion,
      power_saver: !!prefs.powerSaver
    });

    // Debounced save
    const t = setTimeout(async () => {
      try {
        console.log('[UniversePrefs] Saving to backend:', prefs);
        await patchVibe(prefs);
        lastSavedJsonRef.current = json;
        localStorage.setItem(KEY, json);
        console.log('[UniversePrefs] Save successful');
      } catch (e) {
        console.warn('[UniversePrefs] Save failed:', e);
        toast.error('Couldn\'t save vibe preference', { duration: 2000 });
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(t);
  }, [prefs]);

  return { prefs, setPrefs };
}
